import { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, ShieldCheck, AlertTriangle, MonitorPlay,
  CreditCard, MessageSquare, Mail, Key, Bell, LogOut, ExternalLink, Menu, X,
  Crown, Receipt, Globe, Sliders, Gift,
} from "lucide-react";

const NAV = [
  { label: "Overview", items: [{ name: "Dashboard", icon: LayoutDashboard, path: "/admin", end: true }] },
  { label: "Management", items: [
    { name: "Users", icon: Users, path: "/admin/users" },
    { name: "Support Tickets", icon: MessageSquare, path: "/admin/tickets" },
    { name: "ID Verifications", icon: ShieldCheck, path: "/admin/verifications" },
    { name: "Reports Queue", icon: AlertTriangle, path: "/admin/reports" },
  ] },
  { label: "Memberships", items: [
    { name: "Packages", icon: Crown, path: "/admin/packages" },
    { name: "Subscriptions", icon: Receipt, path: "/admin/subscriptions" },
    { name: "Referrals & Coupons", icon: Gift, path: "/admin/referrals" },
  ] },
  { label: "Content", items: [{ name: "Home Page CMS", icon: MonitorPlay, path: "/admin/cms" }] },
  { label: "Integrations & Settings", items: [
    { name: "Core Features", icon: Sliders, path: "/admin/settings/core" },
    { name: "Payment Gateway", icon: CreditCard, path: "/admin/settings/payments" },
    { name: "SMS Providers", icon: MessageSquare, path: "/admin/settings/sms" },
    { name: "Email / SMTP", icon: Mail, path: "/admin/settings/email" },
    { name: "Captcha", icon: Key, path: "/admin/settings/captcha" },
    { name: "Push Notifications", icon: Bell, path: "/admin/settings/push" },
    { name: "Email Notifications", icon: Bell, path: "/admin/settings/notifications" },
    { name: "Google Social Login", icon: Globe, path: "/admin/settings/google" },
  ] },
];

export function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const SidebarInner = (
    <>
      <div className="h-16 flex items-center gap-2 px-6 border-b border-[#3E1016]">
        <img src="/logo.png" alt="Hamarsathi" className="h-8 object-contain brightness-0 invert" />
        <div className="leading-tight">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FDBABF] font-semibold">Admin</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-3">
        {NAV.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.18em] text-[#FDBABF]/70 font-semibold">{group.label}</div>
            <div className="space-y-0.5">
              {group.items.map((it) => (
                <NavLink
                  key={it.path}
                  to={it.path}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  data-testid={`admin-nav-${it.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      isActive ? "bg-[#6B1A24] text-white" : "text-[#FDBABF] hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <it.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                  {it.name}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-2 border-t border-[#3E1016]">
        <a href="/app" className="flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm text-[#FDBABF] hover:bg-white/5 hover:text-white transition-colors" data-testid="admin-view-app">
          <ExternalLink className="w-4 h-4" /> View user app
        </a>
        <button onClick={async () => { await logout(); navigate("/login"); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-md text-sm text-[#FDBABF] hover:bg-white/5 hover:text-white transition-colors" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <aside className="w-64 fixed inset-y-0 left-0 z-50 bg-[#2A0A0E] flex-col border-r border-[#3E1016] hidden lg:flex">{SidebarInner}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#2A0A0E] flex flex-col">{SidebarInner}</div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}

      <header className="h-16 fixed top-0 right-0 left-0 lg:left-64 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center px-4 sm:px-8 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-toggle"><Menu className="w-5 h-5" /></Button>
          <span className="text-sm text-slate-500">Signed in as <span className="font-medium text-slate-800">{user?.email}</span></span>
        </div>
        <span className="text-xs font-semibold text-[#6B1A24] bg-[#6B1A24]/10 px-3 py-1 rounded-full">Administrator</span>
      </header>

      <main className="lg:ml-64 pt-16 min-h-screen">
        <div className="p-4 sm:p-8"><Outlet /></div>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#2A0A0E] tracking-tight" data-testid="admin-page-title">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function SettingsShell({ title, desc, children }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="flex flex-col gap-2 lg:pr-8">
        <h2 className="text-xl font-sans font-semibold text-slate-800">{title}</h2>
        <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
      </div>
      <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 shadow-sm p-6 flex flex-col gap-6">
        {children}
      </div>
    </div>
  );
}
